<script setup>
// Photo/video gallery for the project — reuses the same project documents
// endpoint as the Documents tab, filtered to the 'image'/'video' (+ legacy
// 'walkthrough_video') document_types, customer-shareable only. No separate
// media storage — images/videos are the same Nexceo::ProjectDocument rows
// the admin Documents tab manages, just presented as a gallery. File size is
// shown only when it's real (ActiveStorage-attached uploads expose
// document_file_size); dimensions/video duration aren't stored for
// external-URL documents, so they're never shown here.
import { ref, computed, onMounted, onUnmounted, nextTick, watch } from 'vue';
import projectDocumentsAPI from 'dashboard/api/nexceo/projectDocuments';

const props = defineProps({
  // Unused here — SalesProjectShow passes it to every tab uniformly;
  // declared so Vue doesn't fall it through as a stray DOM attribute.
  project: { type: Object, default: null }, // eslint-disable-line vue/no-unused-properties
  projectId: { type: Number, required: true },
});

const IMAGE_TYPES = ['image'];
const VIDEO_TYPES = ['video', 'walkthrough_video'];

const documents = ref([]);
const isLoading = ref(false);
const error = ref(null);
const searchTerm = ref('');
const activeFilter = ref('all'); // all | images | videos
const lightboxIndex = ref(null);

const shareableMedia = computed(() =>
  documents.value.filter(d => d.customer_shareable && d.shareable_url)
);
const images = computed(() =>
  shareableMedia.value.filter(d => IMAGE_TYPES.includes(d.document_type))
);
const videos = computed(() =>
  shareableMedia.value.filter(d => VIDEO_TYPES.includes(d.document_type))
);
// Deliberately narrower than shareableMedia — this tab only ever renders
// images/videos (immersive docs get their own tab), so every count/empty-
// state check here must match that, not the broader customer-shareable set.
const mediaCount = computed(() => images.value.length + videos.value.length);

function matchesSearch(doc) {
  if (!searchTerm.value.trim()) return true;
  return doc.title
    ?.toLowerCase()
    .includes(searchTerm.value.trim().toLowerCase());
}

const filteredImages = computed(() => images.value.filter(matchesSearch));
const filteredVideos = computed(() => videos.value.filter(matchesSearch));

// Bento grid — one interleaved images+videos grid instead of two separate
// sections, with a repeating variable col/row-span pattern purely for
// visual rhythm (no stored "span"/order field — nothing persisted, so a
// reload always lays out the same way for the same document list).
const filteredMedia = computed(() => {
  if (activeFilter.value === 'images') return filteredImages.value;
  if (activeFilter.value === 'videos') return filteredVideos.value;
  return shareableMedia.value.filter(
    d =>
      matchesSearch(d) &&
      (IMAGE_TYPES.includes(d.document_type) ||
        VIDEO_TYPES.includes(d.document_type))
  );
});
const BENTO_SPAN_PATTERN = [
  'col-span-2 row-span-2',
  'col-span-1 row-span-1',
  'col-span-1 row-span-2',
  'col-span-1 row-span-1',
  'col-span-2 row-span-1',
  'col-span-1 row-span-1',
];
function bentoSpan(index) {
  return BENTO_SPAN_PATTERN[index % BENTO_SPAN_PATTERN.length];
}
function isVideo(doc) {
  return VIDEO_TYPES.includes(doc.document_type);
}

// A video document's URL is sometimes a pasted YouTube link rather than a
// direct file (the admin form's "External URL" field accepts any URL) — an
// HTML <video> tag can't play those, so detect and embed them instead.
const YOUTUBE_PATTERNS = [
  /youtu\.be\/([a-zA-Z0-9_-]{6,})/,
  /youtube\.com\/watch\?(?:.*&)?v=([a-zA-Z0-9_-]{6,})/,
  /youtube\.com\/embed\/([a-zA-Z0-9_-]{6,})/,
  /youtube\.com\/shorts\/([a-zA-Z0-9_-]{6,})/,
];
function youtubeId(url) {
  if (!url) return null;
  const match = YOUTUBE_PATTERNS.map(pattern => url.match(pattern)).find(
    Boolean
  );
  return match ? match[1] : null;
}
function youtubeEmbedUrl(doc) {
  const id = youtubeId(doc.shareable_url);
  return id
    ? `https://www.youtube.com/embed/${id}?rel=0&modestbranding=1`
    : null;
}
function youtubeThumbUrl(doc) {
  const id = youtubeId(doc.shareable_url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : null;
}

// In-grid videos autoplay (muted/looped) only while scrolled into view,
// and pause otherwise — one shared observer rather than one per tile.
const videoRefs = ref({});
function setVideoRef(id, el) {
  if (el) videoRefs.value[id] = el;
  else delete videoRefs.value[id];
}
let mediaObserver = null;
function observeVideos() {
  if (mediaObserver) mediaObserver.disconnect();
  mediaObserver = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        const el = entry.target;
        if (entry.isIntersecting) {
          el.play?.().catch(() => {});
        } else {
          el.pause?.();
        }
      });
    },
    { threshold: 0.4 }
  );
  Object.values(videoRefs.value).forEach(el => mediaObserver.observe(el));
}
watch(filteredMedia, () => {
  nextTick(observeVideos);
});

// Lightbox browses the same unified images+videos list the bento grid
// shows, so clicking any tile (not just images) opens it.
function openLightbox(doc) {
  lightboxIndex.value = filteredMedia.value.findIndex(d => d.id === doc.id);
}
function closeLightbox() {
  lightboxIndex.value = null;
}
const lightboxDoc = computed(() =>
  lightboxIndex.value !== null ? filteredMedia.value[lightboxIndex.value] : null
);
function prevImage() {
  if (lightboxIndex.value === null) return;
  lightboxIndex.value =
    (lightboxIndex.value - 1 + filteredMedia.value.length) %
    filteredMedia.value.length;
}
function nextImage() {
  if (lightboxIndex.value === null) return;
  lightboxIndex.value = (lightboxIndex.value + 1) % filteredMedia.value.length;
}
function onKeydown(e) {
  if (lightboxIndex.value === null) return;
  if (e.key === 'ArrowLeft') prevImage();
  if (e.key === 'ArrowRight') nextImage();
  if (e.key === 'Escape') closeLightbox();
}

function formatSize(doc) {
  if (!doc.document_file_size) return null;
  const kb = doc.document_file_size / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

async function load() {
  isLoading.value = true;
  error.value = null;
  try {
    const { data } = await projectDocumentsAPI.get(props.projectId, {
      status: 'active',
    });
    documents.value = Array.isArray(data) ? data : [];
  } catch {
    error.value = 'Failed to load media.';
  } finally {
    isLoading.value = false;
  }
}

onMounted(() => {
  load();
  window.addEventListener('keydown', onKeydown);
});

onUnmounted(() => {
  window.removeEventListener('keydown', onKeydown);
  mediaObserver?.disconnect();
});
</script>

<template>
  <!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
  <div class="flex-1 overflow-y-auto">
    <div v-if="isLoading" class="flex justify-center py-16">
      <span class="i-lucide-loader-2 size-5 animate-spin text-n-slate-7" />
    </div>

    <div
      v-else-if="error"
      class="m-6 text-sm text-red-600 dark:text-red-400 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30"
    >
      {{ error }}
    </div>

    <div v-else class="flex flex-col gap-6 p-6 max-w-5xl mx-auto">
      <!-- Header -->
      <div class="flex items-center justify-between gap-4 flex-wrap">
        <div class="flex items-center gap-3">
          <span
            class="flex items-center justify-center size-10 rounded-xl bg-woot-50 dark:bg-woot-500/10 shrink-0"
          >
            <span
              class="i-lucide-image size-5 text-woot-600 dark:text-woot-400"
            />
          </span>
          <div>
            <h1 class="text-base font-semibold text-n-slate-12">
              Media Library
              <span class="text-n-slate-7 font-normal">({{ mediaCount }})</span>
            </h1>
            <p class="text-sm text-n-slate-9 mt-0.5">
              Photos and videos ready to present.
            </p>
          </div>
        </div>
      </div>

      <!-- Filters -->
      <div v-if="mediaCount" class="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          class="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
          :class="
            activeFilter === 'all'
              ? 'bg-woot-500 text-white'
              : 'bg-n-alpha-2 text-n-slate-10 hover:bg-n-alpha-3'
          "
          @click="activeFilter = 'all'"
        >
          All ({{ mediaCount }})
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
          :class="
            activeFilter === 'images'
              ? 'bg-woot-500 text-white'
              : 'bg-n-alpha-2 text-n-slate-10 hover:bg-n-alpha-3'
          "
          @click="activeFilter = 'images'"
        >
          Images ({{ images.length }})
        </button>
        <button
          type="button"
          class="px-3 py-1.5 rounded-full text-xs font-medium transition-colors"
          :class="
            activeFilter === 'videos'
              ? 'bg-woot-500 text-white'
              : 'bg-n-alpha-2 text-n-slate-10 hover:bg-n-alpha-3'
          "
          @click="activeFilter = 'videos'"
        >
          Videos ({{ videos.length }})
        </button>
        <div class="flex-1" />
        <div class="relative">
          <span
            class="i-lucide-search absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-n-slate-6 pointer-events-none"
          />
          <input
            v-model="searchTerm"
            type="text"
            placeholder="Search media…"
            class="reset-base pl-8 pr-3 py-1.5 !mb-0 text-xs rounded-lg border border-n-weak bg-n-background text-n-slate-12 outline-none focus:ring-2 focus:ring-woot-500 w-48"
          />
        </div>
      </div>

      <div v-if="!mediaCount" class="text-center py-16">
        <span class="i-lucide-image size-8 text-n-slate-6 mx-auto block mb-2" />
        <p class="text-sm font-medium text-n-slate-9">No media added yet.</p>
      </div>

      <!-- Bento grid — images and videos interleaved, videos autoplay
           (muted/looped) only while scrolled into view. -->
      <TransitionGroup
        v-if="filteredMedia.length"
        tag="div"
        class="grid grid-cols-2 sm:grid-cols-4 auto-rows-[130px] gap-3"
        enter-active-class="transition duration-300 ease-out"
        enter-from-class="opacity-0 scale-95"
        move-class="transition duration-300 ease-out"
      >
        <button
          v-for="(item, index) in filteredMedia"
          :key="item.id"
          type="button"
          class="relative overflow-hidden rounded-xl border border-n-weak group text-left"
          :class="bentoSpan(index)"
          @click="openLightbox(item)"
        >
          <img
            v-if="isVideo(item) && youtubeThumbUrl(item)"
            :src="youtubeThumbUrl(item)"
            :alt="item.title"
            loading="lazy"
            class="absolute inset-0 w-full h-full object-cover"
          />
          <video
            v-else-if="isVideo(item)"
            :ref="el => setVideoRef(item.id, el)"
            :src="item.shareable_url"
            muted
            loop
            playsinline
            preload="metadata"
            class="absolute inset-0 w-full h-full object-cover"
          />
          <img
            v-else
            :src="item.shareable_url"
            :alt="item.title"
            loading="lazy"
            class="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
          <span
            v-if="isVideo(item)"
            class="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <span
              class="flex items-center justify-center size-9 rounded-full bg-black/50 text-white"
            >
              <span class="i-lucide-play size-4" />
            </span>
          </span>
          <div
            class="absolute inset-x-0 bottom-0 p-2 bg-gradient-to-t from-black/70 to-transparent pointer-events-none"
          >
            <p class="text-xs font-medium text-white truncate">
              {{ item.title }}
            </p>
            <p v-if="formatSize(item)" class="text-[11px] text-white/70">
              {{ formatSize(item) }}
            </p>
          </div>
        </button>
      </TransitionGroup>
    </div>

    <!-- Lightbox with filmstrip -->
    <teleport to="body">
      <div
        v-if="lightboxDoc"
        class="fixed inset-0 z-50 flex flex-col bg-black/90"
        @click.self="closeLightbox"
      >
        <!-- Top bar -->
        <div class="flex items-center justify-between px-5 py-3 shrink-0">
          <div class="text-white">
            <p class="text-sm font-medium">{{ lightboxDoc.title }}</p>
            <p class="text-xs text-white/60">
              {{ lightboxIndex + 1 }} / {{ filteredMedia.length }}
              <span v-if="formatSize(lightboxDoc)">
                · {{ formatSize(lightboxDoc) }}
              </span>
            </p>
          </div>
          <div class="flex items-center gap-2">
            <a
              v-if="!youtubeEmbedUrl(lightboxDoc)"
              :href="lightboxDoc.shareable_url"
              :download="lightboxDoc.title"
              target="_blank"
              rel="noopener noreferrer"
              class="flex items-center justify-center size-9 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              title="Download"
            >
              <span class="i-lucide-download size-4" />
            </a>
            <button
              class="flex items-center justify-center size-9 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
              @click="closeLightbox"
            >
              <span class="i-lucide-x size-4" />
            </button>
          </div>
        </div>

        <!-- Main media -->
        <div
          class="flex-1 flex items-center justify-center relative px-16 min-h-0"
          @click.self="closeLightbox"
        >
          <button
            v-if="filteredMedia.length > 1"
            class="absolute left-4 flex items-center justify-center size-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            @click="prevImage"
          >
            <span class="i-lucide-chevron-left size-5" />
          </button>
          <iframe
            v-if="youtubeEmbedUrl(lightboxDoc)"
            :src="youtubeEmbedUrl(lightboxDoc)"
            title="Video"
            frameborder="0"
            allow="autoplay; encrypted-media; picture-in-picture"
            allowfullscreen
            class="w-full max-w-4xl aspect-video rounded"
          />
          <video
            v-else-if="isVideo(lightboxDoc)"
            :src="lightboxDoc.shareable_url"
            controls
            autoplay
            class="max-w-full max-h-full object-contain rounded"
          />
          <img
            v-else
            :src="lightboxDoc.shareable_url"
            :alt="lightboxDoc.title"
            class="max-w-full max-h-full object-contain rounded"
          />
          <button
            v-if="filteredMedia.length > 1"
            class="absolute right-4 flex items-center justify-center size-10 rounded-full bg-white/10 text-white hover:bg-white/20 transition-colors"
            @click="nextImage"
          >
            <span class="i-lucide-chevron-right size-5" />
          </button>
        </div>

        <!-- Dock — floating media selector -->
        <div
          v-if="filteredMedia.length > 1"
          class="flex justify-center pb-5 shrink-0"
        >
          <div
            class="flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-lg overflow-x-auto max-w-full"
          >
            <button
              v-for="(mediaDoc, i) in filteredMedia"
              :key="mediaDoc.id"
              type="button"
              class="relative shrink-0 size-9 sm:size-10 rounded-lg overflow-hidden transition-all duration-200"
              :class="
                i === lightboxIndex
                  ? 'ring-2 ring-white scale-110 z-10'
                  : (i % 2 === 0 ? '-rotate-6' : 'rotate-6') +
                    ' opacity-70 hover:opacity-100 hover:scale-105'
              "
              @click="lightboxIndex = i"
            >
              <img
                v-if="isVideo(mediaDoc) && youtubeThumbUrl(mediaDoc)"
                :src="youtubeThumbUrl(mediaDoc)"
                :alt="mediaDoc.title"
                class="w-full h-full object-cover"
              />
              <video
                v-else-if="isVideo(mediaDoc)"
                :src="mediaDoc.shareable_url"
                muted
                preload="metadata"
                class="w-full h-full object-cover pointer-events-none"
              />
              <img
                v-else
                :src="mediaDoc.shareable_url"
                :alt="mediaDoc.title"
                class="w-full h-full object-cover"
              />
              <span
                v-if="isVideo(mediaDoc)"
                class="absolute inset-0 flex items-center justify-center bg-black/20"
              >
                <span class="i-lucide-play size-3 text-white" />
              </span>
            </button>
          </div>
        </div>
      </div>
    </teleport>
  </div>
</template>
